import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { uploadProjectSelector } from "store/customerProject/selector";
import { updateProjectStatus } from "store/customerProject/reducer";
import { supportApi } from "views/customer/support/hooks/supportApi";

interface UseRevisionRequestReturn {
  isSubmitting: boolean;
  submitRevisionRequest: (revisionText: string, documentId?: number) => Promise<{ success: boolean; message: string }>;
  clearAttachedFiles: () => void;
}

export const useRevisionRequest = (projectManagerIds?: number[]): UseRevisionRequestReturn => {
  const dispatch = useDispatch();
  const { id: orderId } = useParams();
  const uploadProject = useSelector(uploadProjectSelector);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clearAttachedFiles = useCallback(() => {
    dispatch(updateProjectStatus({ documents: [] }));
  }, [dispatch]);

  const submitRevisionRequest = useCallback(
    async (revisionText: string, documentId?: number): Promise<{ success: boolean; message: string }> => {
      if (!orderId) {
        return { success: false, message: "Missing required information" };
      }

      if (!revisionText.trim()) {
        return { success: false, message: "Please provide revision details" };
      }

      setIsSubmitting(true);

      try {
        const attachedFiles = uploadProject?.documents || [];
        const attachments = attachedFiles
          .map((file: any) => file.fileUrl)
          .filter(Boolean);

        const chatPayload: any = {
          topic: "Order support",
          message: revisionText,
          attachments: attachments.length > 0 ? attachments : undefined,
          order_id: Number(orderId),
        };

        // Include assigned_manager_id if project has managers assigned
        if (projectManagerIds && projectManagerIds.length > 0) {
          chatPayload.assigned_manager_id = projectManagerIds[projectManagerIds.length - 1]; // Use last manager ID
        }

        await supportApi.createChat(chatPayload);

        clearAttachedFiles();
        setIsSubmitting(false);
        return { success: true, message: "Revision request submitted successfully" };
      } catch (error: any) {
        console.error("Error submitting revision request:", error);
        setIsSubmitting(false);

        const errorMessage =
          error?.data?.error ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to submit revision request. Please try again.";

        return { success: false, message: errorMessage };
      }
    },
    [orderId, uploadProject, clearAttachedFiles, projectManagerIds]
  );

  return {
    isSubmitting,
    submitRevisionRequest,
    clearAttachedFiles,
  };
};
